import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { spacing } from "@mui/system";
import { fetchApi } from "@/utils/fetchApi";
import { useNavigate } from "react-router-dom";

import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Container,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
} from "@mui/material";

import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { green, red } from "@mui/material/colors";

import HeroSection from "./HeroSection";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);

const Wrapper = styled.div`
  padding-top: 0rem;
  position: relative;
  text-align: center;
  overflow: hidden;
  background: ${(props) => props.theme.palette.background.paper};

  @keyframes imageAnimation {
    from {
      opacity: 0;
      transform: perspective(1500px) rotateX(0deg);
    }

    to {
      opacity: 1;
      transform: perspective(2000px) rotateX(25deg);
    }
  }

  .imageAnimation {
    animation-name: imageAnimation;
  }
`;

// Critical Instruction Detection and Response Algorithm" (CIDARA) is a proprietary technology developed by OnRounds AI

const mainFeaturesArray = [
  {
    productid: "prod_PjkKzAomSCQiAU",
    name: "Basic",
    description:
      "Perfect for individual practitioners, OnRounds Basic offers essential tools for streamlined documentation and patient management. Ideal for those in solo practices or starting their journey in healthcare, it provides the foundational features needed to enhance efficiency while maintaining compliance.",
    features: [
      {
        name: "Speech-to-Text Transcription",
      },
      {
        name: "CIDARA AI Assistant",
      },
      {
        name: "Unlimited Visits",
      },
      {
        name: "Email Support",
      },
      {
        name: "HIPAA Compliant",
      },
    ],
  },
  {
    productid: "prod_Pi6q3l3tpgpemy",
    name: "Professional",
    description:
      "OnRounds Pro is the choice for healthcare professionals aiming to elevate practice efficiency and patient care. Ideal for experienced clinicians and small to medium-sized practices, our Pro version offers advanced features like enhanced EHR/EMR integration and comprehensive analytics.",
    features: [
      {
        name: "Speech-to-Text Transcription",
      },
      {
        name: "CIDARA AI Assistant Pro",
      },
      {
        name: "Unlimited Visits",
      },
      {
        name: "Dedicated 24h Support",
      },
      {
        name: "HIPAA Compliant",
      },
    ],
  },
  {
    productid: "prod_PjkNljYdHLdqj3",
    name: "Enterprise",
    description:
      "OnRounds Enterprise is ideal for large healthcare organizations and is equipped with centralized robust management of feature access and data control for physicians. This scalable solution offers enhanced integration, advanced security, data analytics, and dedicated support.",
    features: [
      {
        name: "Speech-to-Text Transcription",
      },
      {
        name: "CIDARA AI Assistant Pro",
      },
      {
        name: "Unlimited Visits",
      },
      {
        name: "Dedicated 24h Support",
      },
      {
        name: "HIPAA Compliant",
      },
    ],
  },
];

const featuresArray = [
  {
    type: "header",
    name: "Core Features",
  },
  {
    type: "feature",
    name: "Real-Time Voice-To-Text Transcription",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Automated Clinical Note Generation",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Automated Patient Communication",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Transcription and Visit Note Management",
    products: ["y", "y", "y"],
  },
  {
    type: "header",
    name: "Advanced Capabilities",
  },
  {
    type: "feature",
    name: "CIDARA Voice Control",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "AI-Assisted Patient Onboarding",
    products: ["n", "y", "y"],
  },
  {
    type: "feature",
    name: "Specialty-Specific Documentation Templates",
    products: ["n", "y", "y"],
  },
  {
    type: "feature",
    name: "Automated Medical Coding Suggestions",
    products: ["n", "y", "y"],
  },
  {
    type: "feature",
    name: "Computer Vision (CV) Features",
    products: ["n", "y", "y"],
  },
  {
    type: "feature",
    name: "EKG and Cardiac Notes",
    products: ["n", "y", "y"],
  },
  {
    type: "feature",
    name: "ORC MD Integration",
    products: ["n", "+$100/mon", "+$100/mon"],
  },
  {
    type: "header",
    name: "Compliance and Security",
  },
  {
    type: "feature",
    name: "Secure, Double Factor Authentication",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Data Encryption at Rest and in Transit",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Secure, Encrypted Cloud-Based Storage",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "HIPAA Compliant",
    products: ["y", "y", "y"],
  },
  {
    type: "header",
    name: "Integration Services",
  },
  {
    type: "feature",
    name: "Basic EHR/EMR Integration",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Full Automated EHR/EMR Integration",
    products: ["n", "n", "min 10 users"],
  },
  {
    type: "feature",
    name: "Extensible API Access",
    products: ["n", "n", "min 10 users"],
  },
  {
    type: "feature",
    name: "Customer Relationship Management (CRM) Integration",
    products: ["n", "n", "min 10 users"],
  },
  {
    type: "feature",
    name: "Appointment Calendar Integration",
    products: ["n", "n", "min 10 users"],
  },
  {
    type: "feature",
    name: "Telehealth Platform Integration",
    products: ["n", "n", "min 10 users"],
  },
  {
    type: "feature",
    name: "Laboratory Information Systems (LIS) Integration",
    products: ["n", "n", "min 10 users"],
  },
  {
    type: "header",
    name: "Support and Training",
  },
  {
    type: "feature",
    name: "Access To Online Training",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Live Zoom Training",
    products: ["n", "n", "y"],
  },
  {
    type: "feature",
    name: "Email Support",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Live Phone Support",
    products: ["n", "9am - 5pm (CST)", "24h"],
  },
  {
    type: "feature",
    name: "Live Zoom Support",
    products: ["n", "9am - 5pm (CST)", "24h"],
  },
  {
    type: "header",
    name: "Accessibility and Inclusivity",
  },
  {
    type: "feature",
    name: "Internationalization (I18N) Support",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Multilingual and Localization (L10N) Support",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Zero UI/UX Interface for Visually Impaired Users",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Screen Reader Compatibility",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Closed Captioning",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Epilepsy Safe Mode",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Accessibility Testing and Feedback",
    products: ["y", "y", "y"],
  },
  {
    type: "header",
    name: "Mobile Access",
  },
  {
    type: "feature",
    name: "CIDARA Interface for Mobile Devices",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Session Recording and Playback",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Transcription and Note Generation",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Visit Notes Management",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Patient Communication Management",
    products: ["y", "y", "y"],
  },
  {
    type: "header",
    name: "Analytics and Reporting",
  },
  {
    type: "feature",
    name: "Patient Flow Analytics",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Clinic Time Management Reports",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Usage Trends of AI Features",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Billing and Coding Accuracy Reports",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Revenue Cycle Reports",
    products: ["y", "y", "y"],
  },
  {
    type: "feature",
    name: "Custom Reporting",
    products: ["n", "n", "min 10 users"],
  },
];

const ProductSection = ({ products }) => {
  const navigate = useNavigate();

  const handleCreateClick = () => {
    navigate("/auth/sign-up");
  };

  const productDisplay = products.map((item, index) => {
    const backcolor = index % 2 === 0 ? "#f9f9f9" : "#fff";
    const mainFeatures = mainFeaturesArray.find((feature) => feature.productid === item.id);
    return (
      <Grid
        item
        xs={12}
        sm={6}
        md={3}
        key={item.id}
        sx={{ position: "sticky", top: 0, zIndex: 1, pt: 7, background: backcolor }}
      >
        <Typography style={{ fontSize: "1.7rem", color: "#555", textTransform: "uppercase", fontWeight: "bold" }}>
          {item.name.replace("Access", "")}
        </Typography>
        <Typography style={{ fontSize: "1.2rem", color: "#376fd0", fontWeight: "bold" }}>{item.price}</Typography>
        <Typography variant="body2" style={{ color: "#999", fontWeight: "bold" }}>
          PER USER
        </Typography>
        <Box component="ul" sx={{ listStyle: "none", padding: 0, fontSize: "0.8rem", mx: 8 }}>
          {mainFeatures.features.map((feature, index) => (
            <Box component="li" key={index} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CheckIcon sx={{ color: green[700], fontSize: "1rem", mr: 2 }} />
              <Typography color="textSecondary">{feature.name}</Typography>
            </Box>
          ))}
        </Box>
        <Box my={6} px={5} sx={{ mx: 2, textAlign: "left" }}>
          <Typography style={{ fontSize: "10pt", color: "#666666" }}>{mainFeatures.description}</Typography>
        </Box>
        <Box my={6}>
          <Button variant="contained" color="primary" onClick={handleCreateClick}>
            Create Account
          </Button>
        </Box>

        {/*
        <Box my={6}>
          <Typography variant="caption" display="block">
            30-Day Money-Back Guarantee
          </Typography>
        </Box>
        */}
      </Grid>
    );
  });

  return (
    <Grid container>
      <Grid item xs={12} sx={{ textAlign: "left" }}>
        <Box my={8} px={5}>
          <Typography sx={{ fontSize: "1.2rem", color: "#333333" }}>
            OnRounds AI is an integrated suite crafted to empower healthcare professionals. Tailored for the demands of
            modern medicine, OnRounds AI ensures that you can{" "}
            <Box component="span" sx={{ fontWeight: "bold" }}>
              focus on patient care
            </Box>{" "}
            while our technology takes care of the rest.
          </Typography>
        </Box>
      </Grid>
      <Grid item xs={3} />
      {productDisplay}
    </Grid>
  );
};

const FeatureSection = ({ products }) => {
  const renderFeatureIcon = (value) => {
    if (value === "y") {
      return <CheckIcon sx={{ color: green[700], fontSize: "1rem", mr: 2 }} />;
    } else if (value === "n") {
      return <CloseIcon sx={{ color: red[700], fontSize: "1rem", mr: 2 }} />;
    } else {
      return <Typography sx={{ fontSize: "9pt", color: "#666666" }}>{value}</Typography>;
    }
  };

  const featureDisplay = featuresArray.map((feature, index) => {
    if (feature.type === "header") {
      return (
        <Box key={index} sx={{ borderTop: "1px solid #DDD" }}>
          <Grid container>
            <Grid item md={12} sx={{ background: "#F9F9F9" }}>
              <Box my={4} mx={2} sx={{ textAlign: "left" }}>
                <Typography sx={{ fontSize: "12pt", color: "#666666", fontWeight: "bold" }}>{feature.name}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }
    return (
      <Box key={index} sx={{ borderTop: "1px solid #DDD" }}>
        <Grid container>
          <Grid item md={3} sx={{ background: "#fff" }}>
            <Box my={4} mx={2} sx={{ textAlign: "left" }}>
              <Typography>{feature.name}</Typography>
            </Box>
          </Grid>
          {feature.products.map((product, productIndex) => (
            <Grid key={productIndex} item md={3} sx={{ background: productIndex % 2 === 0 ? "#fff" : "#f9f9f9" }}>
              <Box my={4} mx={2}>
                {renderFeatureIcon(product)}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  });

  return featureDisplay;
};

function Pricing() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const response = await fetchApi("stripe", "getProducts", {
        limit: 10,
      });
      const sortedProducts = response
        .map((product) => {
          const price = product.prices[0].unit_amount / 100;
          const type = product.prices[0].type;
          const interval = type === "recurring" ? `/${product.prices[0].recurring.interval}` : "";
          return {
            id: product.id,
            name: product.name,
            price: price,
            displayPrice: `$${price}${interval}`,
            type: type,
          };
        })
        .sort((a, b) => a.price - b.price);

      setProducts(
        sortedProducts.map((product) => ({
          ...product,
          price: product.displayPrice, // convert price back to a string for display
        })),
      );
    }
    fetchData();
  }, []);

  return (
    <Wrapper>
      <Box sx={{ flexGrow: 1, width: "100%", overflowX: "auto" }}>
        <HeroSection />
        <Container>
          <Grid container alignItems="center" justifyContent="center" spacing={4} mb={5}>
            {products && products.length > 0 ? (
              <Grid item xs={12}>
                <Box sx={{ position: "sticky", top: 0, zIndex: 1000, backgroundColor: "white" }}>
                  <ProductSection products={products} />
                </Box>
                <Box sx={{ overflowY: "auto" }}>
                  <FeatureSection products={products} />
                </Box>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", my: 20 }}>
                  <Box sx={{ mb: 5 }}>
                    <img
                      alt="loading"
                      src="/static/img/logos/onrounds-logo-symbol-only.svg"
                      style={{ width: "100px", height: "100px", mb: 5 }}
                    />
                  </Box>
                  <Box sx={{ mb: 5 }}>
                    <Typography sx={{ fontSize: "14pt" }}>Loading...</Typography>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>
    </Wrapper>
  );
}

export default Pricing;
